import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../services/apiBaseUrl';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const normalizeAuthUser = useCallback((rawUser, payload = {}) => {
    if (!rawUser) return null;

    const roles = payload.roles || rawUser.roles || [];
    const permissions = payload.permissions || rawUser.permissions || [];
    const allowedTabs = payload.allowed_tabs || rawUser.allowed_tabs || rawUser.allowedTabs || [];

    return {
      ...rawUser,
      roles,
      permissions,
      allowed_tabs: allowedTabs,
    };
  }, []);

  // Configure axios to include auth token
  axios.defaults.baseURL = API_BASE_URL;

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get('/auth/me');
      const payload = response?.data || {};
      const userData = payload.data || payload.user || null;
      setUser(normalizeAuthUser(userData, payload));
    } catch (error) {
      // Token might be expired, clear it
      logout();
    } finally {
      setLoading(false);
    }
  }, [normalizeAuthUser]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Try to get current user info
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  const login = async (email, password) => {
    try {
      console.log('Attempting login with:', { email, password: '***' });
      const response = await axios.post('/auth/login', { email, password });
      console.log('Login response:', response);
      const payload = response?.data || {};
      const { accessToken, refreshToken, user: userData } = payload;
      const normalizedUser = normalizeAuthUser(userData, payload);

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      setUser(normalizedUser);
      const allowedTabs = Array.isArray(normalizedUser?.allowed_tabs) ? normalizedUser.allowed_tabs : [];
      const isAdminUser = allowedTabs.length > 0
        || normalizedUser?.roles?.some((role) => String(role).toLowerCase().includes('admin'));
      console.log('Login successful, isAdmin:', isAdminUser);
      return { success: true, user: normalizedUser, isAdmin: isAdminUser };
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      const payload = error.response?.data || {};
      const fallbackError = error.response
        ? 'Login failed'
        : 'Connexion API impossible. Verifiez que le backend est demarre et accessible depuis ce reseau.';
      return {
        success: false,
        error: payload.error || fallbackError,
        code: payload.code || null,
        meta: payload.meta || null,
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');

      const response = await axios.post('/auth/refresh', { refreshToken });
      const { accessToken, refreshToken: newRefreshToken } = response.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      return true;
    } catch (error) {
      logout();
      return false;
    }
  };

  const value = {
    user,
    login,
    logout,
    refreshToken,
    refreshUser: fetchUser,
    loading,
    isAuthenticated: !!user,
    isAdmin: (Array.isArray(user?.allowed_tabs) && user.allowed_tabs.length > 0)
      || user?.roles?.some(role => String(role).toLowerCase().includes('admin'))
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};